import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Check, Send, Building2, Tag } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { RiskBanner } from "@/components/shared/RiskBanner";
import { Button } from "@/components/ui/button";
import { Icon } from "@/lib/icons";
import { localizeDeep } from "@/lib/localize";

const defaultBenefits = [
  { title: "Private Telegram VIP", description: "Access to the private INVIXA Telegram VIP community.", icon: "Send" },
  { title: "Priority Education", description: "Priority education content & webinars.", icon: "GraduationCap" },
  { title: "Broker Benefits", description: "Verified partner-broker member benefits.", icon: "Building2" },
  { title: "Performance Reports", description: "Transparent performance reporting.", icon: "LineChart" },
  { title: "Member Support", description: "Dedicated member support.", icon: "ShieldCheck" },
];
const defaultSteps = [
  { step: "1", title: "Create your account", description: "Sign up free and open your member dashboard." },
  { step: "2", title: "Choose your path", description: "Subscribe monthly, verify a partner broker, or apply a valid code." },
  { step: "3", title: "Get activated", description: "Our team activates your VIP and shares private Telegram access." },
];

export default function Vip() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  useSEO("INVIXA VIP", "Join the INVIXA VIP membership for private Telegram community access, education and broker benefits.");
  const [pricing, setPricing] = useState(null);
  const [cms, setCms] = useState({});
  const [vipActive, setVipActive] = useState(false);

  useEffect(() => {
    api.get("/vip/pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/content/vip").then((r) => setCms(r.data.value || {})).catch(() => {});
    if (user) api.get("/vip/status").then((r) => setVipActive(!!r.data.vip?.active)).catch(() => {});
  }, [user]);

  const lc = localizeDeep(cms, lang);
  const hero = lc.hero || {};
  const benefits = (lc.benefits && lc.benefits.length ? lc.benefits.filter((b) => b.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0)) : defaultBenefits);
  const steps = (lc.steps && lc.steps.length ? lc.steps.filter((s) => s.active !== false) : defaultSteps);
  const cta = lc.cta || {};

  const ctaLabel = vipActive ? "View VIP Access" : (cta.cta_label || t("hero_cta_vip"));
  const onCta = () => navigate(user ? "/dashboard/vip-access" : "/signup");

  return (
    <div>
      <section className="relative radial-cyan">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-cyan/30 bg-brand-cyan/10 px-4 py-1.5 text-xs font-mono uppercase tracking-widest text-brand-cyan"><Crown className="h-4 w-4" /> {t("vip_section")}</span>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl font-extrabold text-white">{hero.heading || "INVIXA VIP Membership"}</h1>
          <p className="mt-5 max-w-2xl mx-auto text-slate-400">{hero.description || "A premium membership connecting you to our private Telegram VIP community, education and verified broker benefits. Trading signals remain exclusively in the private channel and are never shown on this website."}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-8">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card-surface rounded-3xl p-8">
            <h2 className="font-display text-2xl font-bold text-white">What's included</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={b.id || i} className="flex items-start gap-3" data-testid={`vip-benefit-${i}`}>
                  <div className="h-9 w-9 rounded-lg bg-brand-cyan/10 flex items-center justify-center shrink-0"><Icon name={b.icon || "Check"} className="h-4.5 w-4.5 text-brand-cyan" /></div>
                  <div><p className="font-medium text-white text-sm">{b.title}</p>{b.description && <p className="text-xs text-slate-400 mt-0.5">{b.description}</p>}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 rounded-2xl border border-brand-cyan/20 bg-brand-cyan/5 p-5 flex items-center gap-4">
              <Send className="h-6 w-6 text-brand-cyan" />
              <p className="text-sm text-slate-300">After activation, eligible members receive a private link to the INVIXA Telegram VIP channel from their dashboard.</p>
            </div>
          </div>
          <div className="card-surface rounded-3xl p-8 border-brand-cyan/20 relative overflow-hidden">
            <div className="absolute inset-0 gradient-brand opacity-[0.07]" />
            <div className="relative">
              <p className="font-mono text-xs uppercase tracking-widest text-brand-cyan">Monthly</p>
              <div className="mt-3 flex items-end gap-2">
                <span className="font-display text-5xl font-extrabold text-white">{pricing ? `${pricing.currency === "USD" ? "$" : ""}${pricing.monthly_price}` : "—"}</span>
                <span className="text-slate-500 mb-2">/ month</span>
              </div>
              <p className="mt-3 text-sm text-slate-400">{cta.description || pricing?.description || "Premium VIP membership."}</p>
              <Button onClick={onCta} className="mt-6 w-full gradient-brand text-brand-navy font-semibold rounded-full py-6" data-testid="vip-join-cta">{ctaLabel}</Button>
              <p className="mt-3 text-center text-[11px] text-slate-600">Online payment coming soon — activation is currently reviewed by our team.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <SectionHeading center eyebrow="How to Join" title={cta.heading || "How to reach VIP"} subtitle="Choose the path that suits you — benefits are configured by our team." />
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={s.id || i} className="card-surface hover-glow rounded-2xl p-7" data-testid={`vip-step-${i}`}>
              <div className="h-10 w-10 rounded-full gradient-brand flex items-center justify-center mb-4 font-display font-bold text-brand-navy">{s.step || i + 1}</div>
              <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-400">{s.description}</p>
            </div>
          ))}
        </div>
      </section>
      <RiskBanner />
    </div>
  );
}
